/**
 * XPBar - persistent account-XP indicator. Reads from /stats and refreshes
 * whenever the parent passes a new `refreshKey` (e.g. on round end). Tap to
 * expand into a "what am I working toward?" card.
 */
import { useEffect, useState } from 'react';
import { getCharacterStats, type CharacterStats } from '../../lib/api';

interface Props {
  charId: string;
  /** Bumping this triggers a re-fetch (e.g. set after round end). */
  refreshKey?: number | string;
  /** Optional override for freshly-broadcast values before /stats returns. */
  overrideLevel?: number;
  overrideCurrent?: number;
  overrideNeeded?: number;
  /** Briefly highlight the bar fill after recent XP gain. */
  boost?: boolean;
}

export function XPBar({ charId, refreshKey, overrideLevel, overrideCurrent, overrideNeeded, boost }: Props) {
  const [stats, setStats] = useState<CharacterStats | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const s = await getCharacterStats(charId);
        if (live) setStats(s);
      } catch { /* stats are nice-to-have for the header */ }
    })();
    return () => { live = false; };
  }, [charId, refreshKey]);

  const level = overrideLevel ?? stats?.accountLevel ?? 1;
  const current = overrideCurrent ?? stats?.accountXpToNext?.current ?? 0;
  const needed = overrideNeeded ?? Math.max(1, stats?.accountXpToNext?.needed ?? 1);
  const pct = Math.min(100, Math.max(0, (current / needed) * 100));

  return (
    <div className="xp-bar-shell">
      <button
        type="button"
        className="xp-bar-host"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`Account level ${level}, ${current} of ${needed} XP. Show progression goals.`}
        title={open ? 'Hide progression goals' : 'Show progression goals'}
      >
        <span className="xp-bar-level">Lv {level}</span>
        <span className="xp-bar-track">
          <span className={`xp-bar-fill${boost ? ' boost' : ''}`} style={{ width: `${pct}%` }} />
        </span>
        <span className="xp-bar-num">{current}/{needed}</span>
      </button>

      {open && (
        <XpGoalPanel stats={stats} level={level} current={current} needed={needed} />
      )}
    </div>
  );
}

function XpGoalPanel({ stats, level, current, needed }: { stats: CharacterStats | null; level: number; current: number; needed: number }) {
  const remaining = Math.max(0, needed - current);
  const locked = (stats?.inventory.unlockHints ?? []).filter((h) => !h.unlocked).slice(0, 4);
  const mastery = (stats?.inventory.mastery ?? []).filter((m) => m.nextHint).slice(0, 3);
  const [selected, setSelected] = useState<GoalDetail | null>(null);

  return (
    <div className="xp-goal-panel" role="dialog" aria-label="Progression goals">
      <div className="xp-goal-head">
        <span>Working toward</span>
        <strong>Level {level + 1}</strong>
        <small>{remaining} XP to go</small>
      </div>

      <div className="xp-goal-track" aria-hidden="true">
        <span style={{ width: `${Math.min(100, Math.max(0, (current / Math.max(1, needed)) * 100))}%` }} />
      </div>

      <div className="xp-goal-wallet">
        <button type="button" onClick={() => setSelected(currencyDetail('embers'))}>
          <b>{stats?.embers ?? 0}</b> Embers
        </button>
        <button type="button" onClick={() => setSelected(currencyDetail('lanterns'))}>
          <b>{stats?.lanterns ?? 0}</b> Lanterns
        </button>
      </div>

      {stats?.season && (
        <div className="xp-season-row" aria-label="Season progress">
          <div className="xp-season-head">
            <strong>Season {stats.season.season} · Tier {stats.season.tier}</strong>
            <small>{stats.season.toNext.current}/{stats.season.toNext.needed} SXP</small>
          </div>
          <div className="xp-goal-track" aria-hidden="true">
            <span
              className="xp-season-fill"
              style={{ width: `${Math.min(100, Math.max(0, (stats.season.toNext.current / Math.max(1, stats.season.toNext.needed)) * 100))}%` }}
            />
          </div>
          <small className="xp-season-next">
            {(() => {
              const next = stats.season.rewards[stats.season.tier];
              return next?.free ? `Next reward: ${next.free.label}` : 'Season pass complete!';
            })()}
          </small>
        </div>
      )}

      {/* "Next unlocks" widget removed — referenced the legacy
       *  totalPoints-based unlock chain (5,000-day grinds for Crystal /
       *  Storm cloud / Rose hue). Players now track unlocks through the
       *  Season Pass + Lantern Shop, which surface the same items with
       *  their actual reachable acquisition methods (catalog source). */}
      <GoalList
        title="Game mastery"
        empty="Play a few games to start mastery goals."
        items={mastery.map((m) => ({
          key: m.gameId,
          label: m.nextLabel ?? m.gameName,
          detail: m.nextHint ?? '',
          onInspect: () => setSelected(masteryDetail(m)),
        }))}
      />

      {selected && (
        <div className="xp-goal-detail" aria-live="polite">
          <div>
            <span>{selected.kind}</span>
            <strong>{selected.title}</strong>
          </div>
          <p>{selected.body}</p>
          <small>{selected.action}</small>
        </div>
      )}
    </div>
  );
}

interface GoalDetail {
  kind: string;
  title: string;
  body: string;
  action: string;
}

function currencyDetail(kind: 'embers' | 'lanterns'): GoalDetail {
  if (kind === 'embers') {
    return {
      kind: 'Soft currency',
      title: 'Embers',
      body: 'Embers are the earnable currency planned for common cosmetics, banners, particles, and extra daily challenge rerolls. They do not affect your in-game power.',
      action: 'Earn them through wins, achievements, daily challenges, and future event rewards.',
    };
  }
  return {
    kind: 'Premium currency',
    title: 'Lanterns',
    body: 'Lanterns are planned for premium cosmetics and pass tiers. They are for looks and account flair, not gameplay advantages.',
    action: 'The shop/pass screen is not live yet, so your Lantern balance is being tracked for that future system.',
  };
}

function unlockDetail(hint: NonNullable<CharacterStats['inventory']['unlockHints']>[number]): GoalDetail {
  const copy = unlockKindCopy(hint.kind);
  return {
    kind: copy.kind,
    title: hint.label,
    body: copy.body,
    action: `How to get it: ${goalLine(hint.hint, hint.progress)}${hint.progress?.label === 'profile points' ? ' Profile points come from round results and feed long-term unlocks.' : ''}`,
  };
}

function masteryDetail(mastery: NonNullable<CharacterStats['inventory']['mastery']>[number]): GoalDetail {
  return {
    kind: 'Mastery title',
    title: mastery.nextLabel ?? `${mastery.gameName} mastery`,
    body: `This gives you a title tied to ${mastery.gameName}. Titles show on your character profile and under your name in the lobby once selected. This is not a separate seat-flair cosmetic.`,
    action: `How to get it: ${mastery.nextHint ?? 'Keep playing this game.'}`,
  };
}

function goalLine(hint: string, progress?: { current: number; target: number; label: string }): string {
  if (!progress) return hint;
  const current = Math.min(progress.current, progress.target);
  return `${hint} ${current}/${progress.target} ${progress.label}.`;
}

function unlockKindCopy(kind: NonNullable<CharacterStats['inventory']['unlockHints']>[number]['kind']): { kind: string; body: string } {
  switch (kind) {
    case 'body':
      return { kind: 'Character body', body: 'This unlocks a new body shape for your character. You can equip it from Customize, and other players see it in the lobby and during room moments.' };
    case 'hat':
      return { kind: 'Hat cosmetic', body: 'This unlocks a hat or head accessory for your character. It is cosmetic only and shows on your avatar after you equip it.' };
    case 'face':
      return { kind: 'Face cosmetic', body: 'This unlocks a new expression for your character. It changes how your avatar looks, without changing gameplay.' };
    case 'hue':
      return { kind: 'Color', body: 'This unlocks a new main color for your character. Use it in Customize to change your avatar palette.' };
    case 'accentHue':
      return { kind: 'Accent color', body: 'This unlocks a secondary color option for your character details. Use it in Customize once unlocked.' };
    case 'title':
      return { kind: 'Title', body: 'This unlocks a nameplate title. Titles can be selected for your profile and appear under your name in the lobby. They are not colorable seat-flair effects.' };
    case 'emote':
      return { kind: 'Reaction emote', body: 'This unlocks an extra reaction for the in-game reaction picker. Other players can see it when you send it.' };
    default:
      return { kind: 'Unlock', body: 'This adds a new account reward you can use from the related customization screen.' };
  }
}

function GoalList({ title, empty, items }: { title: string; empty: string; items: Array<{ key: string; label: string; detail: string; onInspect: () => void }> }) {
  return (
    <section className="xp-goal-section">
      <h4>{title}</h4>
      {items.length === 0 ? (
        <p>{empty}</p>
      ) : (
        <ul>
          {items.map((item) => (
            <li key={item.key}>
              <button type="button" onClick={item.onInspect}>
                <strong>{item.label}</strong>
                <span>{item.detail}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
